// Home's KPI row (DC-51, H-9): his five cards with the BRD's definitions — Open Requisitions · Active Candidates · Time to
// Acceptance (Time to Fill beneath) · Offers Accepted · Req health — each a drill-down to its report tab (DS row 36 "a KPI
// may be a drill-down link"). The figures are S13's `kpis` widget data, the same totals report.run gives for the same
// spec, over the reader's own reporting scope. A figure whose producer has not merged is drawn "—", never 0 (PLAN D2).
// KPIs never export (PLAN D9).

import { KpiRow, Kpi, Ring, Widget } from "@/mc";
import { METRIC_LABELS } from "@/mc/g3/labels";
import { reportHref } from "@/views/b10/reports/spec";
import { DASH, figure, kpisOf, seatsOf, type KpiItem, type KpisData } from "./api";
import type { HomeWidgetView } from "./host";
import { BAND_ORDER, BAND_WORDS, HOME_TEMPLATES, KPI_WORDS } from "./labels";
import { hsKey } from "@/views/b10/lib/hsKey";

/** DC-51's order. */
export const KPI_ORDER = ["open_requisitions", "active_candidates", "time_to_acceptance", "offers_accepted", "req_health"] as const;

/** The report tab each card drills to when the server sent no route of its own. */
const KPI_FAMILY: Readonly<Record<string, string>> = {
  open_requisitions: "portfolio",
  active_candidates: "pipeline",
  time_to_acceptance: "portfolio",
  offers_accepted: "offer",
  req_health: "portfolio",
};

const available = (item: KpiItem) => item.status !== "not_yet_available" && item.value !== null;

/** The drill-down of a card: the server's route, else its report tab over the same period. */
export function kpiHref(item: KpiItem, period: KpisData["period"]): string {
  if (item.route) return item.route;
  return reportHref(KPI_FAMILY[item.key] ?? "portfolio", period ? { period } : {});
}

function sub(item: KpiItem) {
  if (item.key === "time_to_acceptance") {
    const ttf = item.detail?.time_to_fill;
    return (
      <>
        {METRIC_LABELS.timeToFill}
        {" · "}
        {figure(ttf, "days")}
        {ttf !== null && ttf !== undefined ? <>{" "}{METRIC_LABELS.days}</> : null}
      </>
    );
  }
  if (item.key === "req_health" && item.detail?.bands) {
    const bands = item.detail.bands;
    return (
      <>
        {BAND_ORDER.map((band, index) => (
          <span key={band}>
            {index > 0 ? " · " : null}
            {BAND_WORDS[band]}
            {" "}
            {bands[band]}
          </span>
        ))}
      </>
    );
  }
  return undefined;
}

function lead(item: KpiItem) {
  const pct = item.detail?.within_target_pct;
  if (item.key !== "time_to_acceptance" || pct === null || pct === undefined) return undefined;
  return <Ring size={44} pct={pct} label={HOME_TEMPLATES.withinTargets({ pct: figure(pct, "percent") })} />;
}

export function KpiCards({ data }: { data: KpisData }) {
  const items = KPI_ORDER.map((key) => data.items.find((item) => item.key === key)).filter((item): item is KpiItem => item !== undefined);
  return (
    <KpiRow>
      {items.map((item) => {
        const ok = available(item);
        return (
          <Kpi
            key={item.key}
            label={KPI_WORDS[item.key] ?? ""}
            value={ok ? figure(item.value, item.unit) : DASH}
            unit={ok && item.unit === "days" ? METRIC_LABELS.days : undefined}
            sub={sub(item)}
            lead={ok ? lead(item) : undefined}
            href={kpiHref(item, data.period)}
            hs={hsKey("dash.kpis", item.key)}
          />
        );
      })}
    </KpiRow>
  );
}

/** Widget `kpis` (S13 → S16, frozen). */
export const Kpis: HomeWidgetView = ({ widgetKey, data }) => {
  const kpis = kpisOf(data);
  if (!kpis.items.some((item) => KPI_WORDS[item.key])) return null;
  return (
    <Widget id={widgetKey} kind="kpis" roles={seatsOf(widgetKey)} tight>
      <KpiCards data={kpis} />
    </Widget>
  );
};
